import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { ScrollText, Loader2 } from "lucide-react";

interface AuditEntry {
  id: string;
  action: string;
  actorName: string | null;
  summary: string;
  createdAt: string;
}

const ACTION_DOT: Record<string, string> = {
  status_change: "bg-blue-400",
  payment: "bg-emerald-400",
  refund: "bg-amber-400",
  invoice_issued: "bg-violet-400",
  sign: "bg-indigo-400",
  cancel: "bg-red-400",
  supplier_cancel: "bg-orange-400",
};

/** Read-only audit trail for an order (Block 2 B2.7) — money/status/signature/cancel events. */
export default function AuditTrailPanel({ orderId }: { orderId: string }) {
  const audit = useQuery({
    queryKey: ["order-audit", orderId],
    queryFn: () => api.get<{ data: AuditEntry[] }>(`/api/admin/orders/${orderId}/audit`),
  });

  const entries = audit.data?.data ?? [];

  return (
    <div className="card">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
        <ScrollText className="h-5 w-5" /> Audit trail
      </h2>
      {audit.isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : entries.length === 0 ? (
        <p className="py-2 text-sm text-gray-500">No audit events yet.</p>
      ) : (
        <ol className="space-y-2">
          {entries.map((e) => (
            <li key={e.id} className="flex gap-3 text-sm">
              <span
                className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${ACTION_DOT[e.action] || "bg-gray-300"}`}
              />
              <div className="min-w-0 flex-1">
                <div className="text-gray-800 dark:text-gray-200">{e.summary}</div>
                <div className="text-xs text-gray-400">
                  {e.actorName || "System"} · {new Date(e.createdAt).toLocaleString("en-GB")}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
